import React, { useRef } from 'react';
import { SectionProps } from '../types/SectionProps';
import { Box, Container, Typography, Card, CardContent, Avatar, Rating } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

const TestimonialsSection: React.FC<SectionProps> = ({ id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const testimonials = [
    {
      name: 'Sophie Mercier',
      role: 'Investisseur immobilier',
      avatar: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=300',
      rating: 5,
      text: "Armelle a su m'accompagner avec bienveillance et professionnalisme dans mon projet d'investissement locatif. Sa vision globale et ses conseils personnalisés m'ont permis de faire les bons choix en toute sérénité."
    },
    {
      name: 'Thomas et Julie Dupont',
      role: 'Famille',
      avatar: 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=300',
      rating: 5,
      text: "Nous cherchions une maison familiale depuis des mois sans succès. Grâce à l'écoute attentive d'Armelle et son réseau, nous avons trouvé notre havre de paix. Un accompagnement humain et sur-mesure qui a fait toute la différence."
    },
    {
      name: 'Marc Leroy',
      role: 'Chef d\'entreprise',
      avatar: 'https://images.pexels.com/photos/1036627/pexels-photo-1036627.jpeg?auto=compress&cs=tinysrgb&w=300',
      rating: 5,
      text: "La gestion patrimoniale proposée par Bonavia a transformé ma vision de l'investissement. Armelle a su structurer mon patrimoine avec clarté et efficacité, tout en restant accessible et réactive à chaque étape."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <Box
      id={id}
      ref={ref}
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: 'background.default',
        scrollMarginTop: '64px', // Pour le défilement fluide
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' },
              position: 'relative',
              display: 'inline-block'
            }}
          >
            Témoignages
            <Box 
              sx={{ 
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '40px',
                height: '3px',
                bgcolor: 'error.main'
              }} 
            />
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              maxWidth: '700px', 
              mx: 'auto', 
              mt: 4,
              color: 'text.secondary',
              fontSize: '1.1rem'
            }}
          >
            Découvrez ce que nos clients disent de notre accompagnement et de notre approche humaine.
          </Typography>
        </Box>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 4 }}>
            {testimonials.map((testimonial, index) => (
              <Box key={index}>
                <motion.div variants={itemVariants}>
                  <Card
                    className="testimonial-card"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '4px',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.05)',
                      position: 'relative',
                      overflow: 'visible',
                      p: 3,
                    }}
                  >
                    <FormatQuoteIcon 
                      sx={{ 
                        position: 'absolute',
                        top: -20,
                        left: 20,
                        fontSize: 60,
                        color: 'secondary.main',
                        opacity: 0.3
                      }} 
                    />
                    <CardContent sx={{ flexGrow: 1, p: 0, pt: 2 }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          mb: 3,
                          fontStyle: 'italic',
                          color: 'text.secondary',
                          lineHeight: 1.7
                        }}
                      >
                        "{testimonial.text}"
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                        <Avatar 
                          src={testimonial.avatar} 
                          alt={testimonial.name}
                          sx={{ 
                            width: 56, 
                            height: 56,
                            border: '2px solid',
                            borderColor: 'secondary.main'
                          }}
                        />
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {testimonial.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {testimonial.role}
                          </Typography>
                          <Rating 
                            value={testimonial.rating} 
                            readOnly 
                            size="small" 
                            sx={{ mt: 0.5, color: 'secondary.main' }}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Box>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
